import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorBanner,
  FrmIcon,
  IconButton,
  MetricStrip,
  Modal,
  PageHeader,
  RefreshButton,
  SegmentedControl,
  Skeleton,
  StatusPill,
  Surface,
  TableFilterBar,
  Tooltip,
  WorkbenchPanel,
  useToast,
  type DataTableColumn,
  type TableFilterField,
  type TableFilterValues,
} from '@frm/ui'
import {
  fetchAgentLogs,
  fetchAgentsHealth,
  pullAgent,
  releaseAgent,
  restartAgent,
  type AgentHealth,
  type AgentLogs,
} from '../api/agents'

type ViewMode = 'cards' | 'table'
type AgentFilter = 'all' | 'issues' | 'down' | 'stale' | 'error' | 'active'
type AgentActionKind = 'restart' | 'release' | 'pull'
type PendingAgentAction = { kind: AgentActionKind; agentId: string } | null

const AGENT_FILTER_FIELDS: TableFilterField[] = [{
  key: 'state',
  kind: 'radio',
  label: 'Состояние',
  options: [
    { value: 'all', label: 'Все' },
    { value: 'issues', label: 'Проблемы' },
    { value: 'down', label: 'Down' },
    { value: 'stale', label: 'Устарели' },
    { value: 'error', label: 'Ошибка' },
    { value: 'active', label: 'Активны' },
  ],
}]

export function AgentsPage() {
  const [view, setView] = useState<ViewMode>('cards')
  const [filter, setFilter] = useState<AgentFilter>('all')
  const [logsAgent, setLogsAgent] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<PendingAgentAction>(null)
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const healthQuery = useQuery({
    queryKey: ['agents', 'health'],
    queryFn: fetchAgentsHealth,
    refetchInterval: 30_000,
  })
  const restartMutation = useMutation({
    mutationFn: restartAgent,
    onSuccess: (result) => {
      toast({
        title: result.result === 'restarted' ? 'Агент перезапущен' : 'Рестарт не выполнен',
        description: `${result.agentId}: ${result.message} · ${result.serviceStatus}`,
        tone: result.result === 'restarted' ? 'success' : 'warning',
      })
      void queryClient.invalidateQueries({ queryKey: ['agents', 'health'] })
    },
    onError: (error) => {
      toast({
        title: 'Рестарт не выполнен',
        description: error instanceof Error ? error.message : 'unknown error',
        tone: 'danger',
      })
    },
  })
  const releaseMutation = useMutation({
    mutationFn: releaseAgent,
    onSuccess: (result) => {
      toast({
        title: result.result === 'ok' ? 'Выпуск выполнен' : 'Выпуск не выполнен',
        description: `${result.agentId}: ${result.message}`,
        tone: result.result === 'ok' ? 'success' : 'warning',
      })
      void queryClient.invalidateQueries({ queryKey: ['agents', 'health'] })
    },
    onError: (error) => {
      toast({
        title: 'Выпуск не выполнен',
        description: error instanceof Error ? error.message : 'unknown error',
        tone: 'danger',
      })
    },
  })
  const pullMutation = useMutation({
    mutationFn: pullAgent,
    onSuccess: (result) => {
      toast({
        title: result.result === 'ok' ? 'Подтягивание выполнено' : 'Подтягивание не выполнено',
        description: `${result.agentId}: ${result.message}`,
        tone: result.result === 'ok' ? 'success' : 'warning',
      })
      void queryClient.invalidateQueries({ queryKey: ['agents', 'health'] })
    },
    onError: (error) => {
      toast({
        title: 'Подтягивание не выполнено',
        description: error instanceof Error ? error.message : 'unknown error',
        tone: 'danger',
      })
    },
  })

  const agents = healthQuery.data?.agents ?? []
  const filteredAgents = useMemo(() => filterAgents(agents, filter), [agents, filter])
  const updatedAt = healthQuery.data?.updatedAt
  const summary = useMemo(() => summarizeAgents(agents), [agents])
  const requestAction = (kind: AgentActionKind, agentId: string) => setPendingAction({ kind, agentId })
  const pendingCopy = pendingAction ? actionCopy(pendingAction.kind, pendingAction.agentId) : null
  const confirmPendingAction = () => {
    if (!pendingAction) return
    const { kind, agentId } = pendingAction
    setPendingAction(null)
    if (kind === 'restart') restartMutation.mutate(agentId)
    if (kind === 'release') releaseMutation.mutate(agentId)
    if (kind === 'pull') pullMutation.mutate(agentId)
  }

  const content = healthQuery.isLoading ? (
    <AgentsSkeleton />
  ) : healthQuery.isError ? (
    <ErrorBanner
      title="Не удалось загрузить health"
      description={healthQuery.error instanceof Error ? healthQuery.error.message : 'unknown error'}
      action={<Button onClick={() => void healthQuery.refetch()}>Повторить</Button>}
    />
  ) : agents.length === 0 ? (
    <EmptyState title="Нет записей health" description="Backend вернул пустой список агентов." />
  ) : filteredAgents.length === 0 ? (
    <EmptyState
      title="Нет агентов по фильтру"
      description="Снимите фильтр или выберите другой сегмент состояния."
      action={<Button size="sm" variant="secondary" onClick={() => setFilter('all')}>Показать все</Button>}
    />
  ) : view === 'cards' ? (
    <AgentCards
      agents={filteredAgents}
      restarting={restartMutation.variables}
      restartPending={restartMutation.isPending}
      onLogs={setLogsAgent}
      onRestart={(name) => requestAction('restart', name)}
      releasing={releaseMutation.variables}
      releasePending={releaseMutation.isPending}
      pulling={pullMutation.variables}
      pullPending={pullMutation.isPending}
      onRelease={(name) => requestAction('release', name)}
      onPull={(name) => requestAction('pull', name)}
    />
  ) : (
    <AgentsTable
      agents={filteredAgents}
      restarting={restartMutation.variables}
      restartPending={restartMutation.isPending}
      onLogs={setLogsAgent}
      onRestart={(name) => requestAction('restart', name)}
      releasing={releaseMutation.variables}
      releasePending={releaseMutation.isPending}
      pulling={pullMutation.variables}
      pullPending={pullMutation.isPending}
      onRelease={(name) => requestAction('release', name)}
      onPull={(name) => requestAction('pull', name)}
    />
  )

  return (
    <>
      <WorkbenchPanel>
        <PageHeader
          dense
          title="Агенты"
          description={`обновлено ${updatedAt ? formatDateTime(updatedAt) : '...'}`}
          actions={
            <div className="agents-page-actions" style={agentsPageActionsStyle}>
              <SegmentedControl
                value={view}
                onChange={setView}
                ariaLabel="Вид списка агентов"
                size="sm"
                items={[
                  { id: 'cards', label: 'Карточки', icon: <CardsIcon /> },
                  { id: 'table', label: 'Таблица', icon: <TableIcon /> },
                ]}
              />
              <RefreshButton
                state={healthQuery.isFetching ? 'refreshing' : 'idle'}
                onRefresh={() => void healthQuery.refetch()}
                label="Обновить"
                size="md"
              />
            </div>
          }
          style={agentsHeaderStyle}
        />
        <MetricStrip
          minColumnWidth={116}
          density="compact"
          cardLayout="row"
          items={[
            { id: 'total', label: 'всего', value: summary.total, tone: 'neutral' },
            { id: 'active', label: 'активны', value: summary.active, tone: 'success' },
            { id: 'stale', label: 'устарели', value: summary.stale, tone: 'warning' },
            { id: 'error', label: 'ошибка', value: summary.error, tone: 'danger' },
            { id: 'down', label: 'down', value: summary.down, tone: 'danger' },
          ]}
          style={metricStripStyle}
        />
        <TableFilterBar
          fields={AGENT_FILTER_FIELDS}
          values={{ state: filter }}
          onChange={(next: TableFilterValues) => {
            const state = next.state
            setFilter(state === 'issues' || state === 'down' || state === 'stale' || state === 'error' || state === 'active' ? state : 'all')
          }}
          onReset={() => setFilter('all')}
          resetLabel="Сбросить"
          activeFiltersLabel="Фильтр агентов"
          hideActiveChips
          density="compact"
          actions={<span style={filterCountStyle}>{filteredAgents.length} из {agents.length}</span>}
          layout={{ maxRows: 1 }}
        />
        <div style={{ marginTop: 22 }}>{content}</div>
        <div style={footnoteStyle}>
          <span style={footnoteDotStyle} />
          Health: systemd + heartbeat каждые 30с · активность = последний успешный запрос · scrub секретов на backend
        </div>
      </WorkbenchPanel>

      <LogsModal agentId={logsAgent} onClose={() => setLogsAgent(null)} />
      <ConfirmDialog
        open={!!pendingAction}
        title={pendingCopy?.title ?? 'Подтверждение'}
        description={pendingCopy?.description}
        tone={pendingCopy?.danger ? 'danger' : 'default'}
        busy={restartMutation.isPending || releaseMutation.isPending || pullMutation.isPending}
        confirmLabel={pendingCopy?.confirm ?? 'Подтвердить'}
        cancelLabel="Отмена"
        onConfirm={confirmPendingAction}
        onCancel={() => setPendingAction(null)}
      >
        <Surface variant="subtle" padding="sm" style={{ display: 'grid', gap: 8 }}>
          <span style={microLabelStyle}>Агент</span>
          <Mono>{pendingAction?.agentId ?? ''}</Mono>
          <span style={{ color: 'var(--frm-text-3)', fontSize: 12.5, lineHeight: 1.45 }}>
            {pendingCopy?.body}
          </span>
        </Surface>
      </ConfirmDialog>
    </>
  )
}

function AgentCards({
  agents,
  restarting,
  restartPending,
  onLogs,
  onRestart,
  releasing,
  releasePending,
  pulling,
  pullPending,
  onRelease,
  onPull,
}: {
  agents: AgentHealth[]
  restarting?: string
  restartPending: boolean
  onLogs: (agentId: string) => void
  onRestart: (agentId: string) => void
  releasing?: string
  releasePending: boolean
  pulling?: string
  pullPending: boolean
  onRelease: (agentId: string) => void
  onPull: (agentId: string) => void
}) {
  const [expanded, setExpanded] = useState<string | null>(null)
  return (
    <div className="agents-card-grid" style={agentCardsGridStyle}>
      {agents.map((agent) => {
        const key = agentKey(agent)
        const isExpanded = expanded === key
        return (
          <article key={key} style={agentCardStyle}>
            <div style={agentCardHeadStyle}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                <div style={agentNameRowStyle}>
                  <Link to={`/agents/${encodeURIComponent(agent.name)}`} style={agentNameLinkStyle}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{agent.name}</span>
                    <FrmIcon name="chevron" size={12} style={{ opacity: 0.7, transform: 'rotate(-90deg)' }} />
                  </Link>
                  <TelegramLink agent={agent} />
                </div>
                <span style={agentMetaStyle}>{agent.role} · {agent.engine}</span>
              </div>
              <AgentStatus agent={agent} />
            </div>

            <div style={agentCardDividerStyle} />

            <SpecGrid agent={agent} />
            <StatusReasonLine agent={agent} />
            <ErrorLine error={agent.error} />
            {isExpanded ? <ExpandedAgent agent={agent} onLogs={onLogs} /> : null}

            <div style={agentCardFootStyle}>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                <Tooltip content="Открыть логи">
                  <IconButton
                    icon={<LogIcon />}
                    label={`Логи ${agent.name}`}
                    size="md"
                    variant="ghost"
                    style={agentIconButtonStyle}
                    onClick={() => onLogs(agent.name)}
                  />
                </Tooltip>
                <Tooltip content="Перезапустить">
                  <IconButton
                    icon={
                      <span
                        aria-hidden
                        style={{
                          display: 'inline-flex',
                          transformOrigin: 'center',
                          animation: restartPending && restarting === agent.name ? 'frm-spin 800ms linear infinite' : undefined,
                        }}
                      >
                        <FrmIcon name="refresh" size={17} />
                      </span>
                    }
                    label={`Перезапустить ${agent.name}`}
                    size="md"
                    variant="ghost"
                    style={agentIconButtonStyle}
                    disabled={restartPending}
                    onClick={() => onRestart(agent.name)}
                  />
                </Tooltip>
                <VersionActionButton
                  agent={agent}
                  releasing={releasePending && releasing === agent.name}
                  pulling={pullPending && pulling === agent.name}
                  onRelease={onRelease}
                  onPull={onPull}
                />
              </span>
              <Button
                variant="ghost"
                size="sm"
                rightIcon={<ChevronDownIcon open={isExpanded} />}
                onClick={() => setExpanded(isExpanded ? null : key)}
                style={agentExpandButtonStyle}
              >
                {isExpanded ? 'Свернуть' : 'Развернуть'}
              </Button>
            </div>
          </article>
        )
      })}
    </div>
  )
}

function AgentsTable({
  agents,
  restarting,
  restartPending,
  onLogs,
  onRestart,
  releasing,
  releasePending,
  pulling,
  pullPending,
  onRelease,
  onPull,
}: {
  agents: AgentHealth[]
  restarting?: string
  restartPending: boolean
  onLogs: (agentId: string) => void
  onRestart: (agentId: string) => void
  releasing?: string
  releasePending: boolean
  pulling?: string
  pullPending: boolean
  onRelease: (agentId: string) => void
  onPull: (agentId: string) => void
}) {
  const columns: DataTableColumn<AgentHealth>[] = [
    {
      key: 'name',
      header: 'Агент',
      width: 168,
      render: (agent) => <AgentNameCell agent={agent} />,
    },
    { key: 'role', header: 'Роль', width: 104, render: (agent) => <span style={tableTextStyle}>{agent.role}</span> },
    { key: 'engine', header: 'Движок', width: 120, render: (agent) => <span style={tableTextStyle}>{agent.engine}</span> },
    { key: 'model', header: 'Модель', width: 148, render: (agent) => <Mono style={modelMonoStyle}>{agent.model}</Mono> },
    {
      key: 'version',
      header: 'Версия→прод',
      width: 184,
      render: (agent) => (
        <VersionAction
          agent={agent}
          compact
          releasing={releasePending && releasing === agent.name}
          pulling={pullPending && pulling === agent.name}
          onRelease={onRelease}
          onPull={onPull}
        />
      ),
    },
    { key: 'pid', header: 'PID', width: 80, render: (agent) => <Mono style={tablePidStyle}>{agent.pid}</Mono>, align: 'right' },
    {
      key: 'service',
      header: <ColumnHeader label="Процесс" hint="systemctl is-active ai-agents-bot@<agent>.service" />,
      width: 112,
      render: (agent) => <ServicePill agent={agent} />,
    },
    {
      key: 'state',
      header: <ColumnHeader label="Здоровье" hint="Итог из systemd, heartbeat и текущей ошибки агента." />,
      width: 112,
      render: (agent) => <AgentStatus agent={agent} />,
    },
    {
      key: 'reason',
      header: <ColumnHeader label="Причина" hint="Диагностика: почему агент считается active, stale, error или down." />,
      width: 174,
      truncate: true,
      render: (agent) => <StatusReasonCell agent={agent} />,
    },
    {
      key: 'activity',
      header: <ColumnHeader label="Последний ответ" hint="Возраст последнего успешно обработанного запроса. Heartbeat процесса показан в tooltip." />,
      width: 126,
      render: (agent) => <ActivityPill agent={agent} />,
    },
    {
      key: 'error',
      header: 'Последняя ошибка',
      width: 190,
      truncate: true,
      render: (agent) => <TableErrorCell error={agent.error} />,
    },
    {
      key: 'logs',
      header: 'Лог',
      width: 58,
      align: 'center',
      render: (agent) => (
        <Tooltip content="Открыть логи">
          <IconButton
            icon={<LogIcon />}
            label={`Логи ${agent.name}`}
            size="lg"
            variant="ghost"
            style={tableIconButtonStyle}
            onClick={() => onLogs(agent.name)}
          />
        </Tooltip>
      ),
    },
    {
      key: 'restart',
      header: 'Рестарт',
      width: 78,
      align: 'center',
      render: (agent) => (
        <Tooltip content="Перезапустить">
          <IconButton
            icon={
              <span
                aria-hidden
                style={{
                  display: 'inline-flex',
                  transformOrigin: 'center',
                  animation: restartPending && restarting === agent.name ? 'frm-spin 800ms linear infinite' : undefined,
                }}
              >
                <FrmIcon name="refresh" size={17} />
              </span>
            }
            label={`Перезапустить ${agent.name}`}
            size="lg"
            variant="ghost"
            style={tableIconButtonStyle}
            disabled={restartPending}
            onClick={() => onRestart(agent.name)}
          />
        </Tooltip>
      ),
    },
  ]
  return (
    <DataTable
      rows={agents}
      columns={columns}
      rowKey={agentKey}
      density="compact"
      ariaLabel="Health агентов"
      emptyTitle="Нет записей health"
    />
  )
}

function LogsModal({ agentId, onClose }: { agentId: string | null; onClose: () => void }) {
  const logsQuery = useQuery({
    queryKey: ['agents', agentId, 'logs', 50],
    queryFn: () => fetchAgentLogs(agentId ?? '', 50),
    enabled: !!agentId,
    refetchInterval: agentId ? 30_000 : false,
  })
  const logs = logsQuery.data as AgentLogs | undefined
  return (
    <Modal
      open={!!agentId}
      onClose={onClose}
      title={agentId ? `Логи · ${agentId}` : 'Логи'}
      description={logs ? `${logs.unit} · последние ${logs.limit} · scrubbed=${String(logs.scrubbed)}` : 'journalctl tail'}
      size="lg"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button loading={logsQuery.isFetching} onClick={() => void logsQuery.refetch()}>
            Обновить
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Закрыть
          </Button>
        </div>
      }
    >
      {logsQuery.isLoading ? (
        <Skeleton height={240} />
      ) : logsQuery.isError ? (
        <ErrorBanner
          title="Логи недоступны"
          description={logsQuery.error instanceof Error ? logsQuery.error.message : 'unknown error'}
        />
      ) : (
        <pre style={logStyle}>{logs?.text || 'Нет строк журнала.'}</pre>
      )}
    </Modal>
  )
}

function actionCopy(kind: AgentActionKind, agentId: string) {
  if (kind === 'restart') {
    return {
      title: 'Перезапустить агента',
      description: `systemd restart для ${agentId}`,
      body: 'Текущая задача агента может быть прервана. После рестарта health обновится следующим polling.',
      confirm: 'Перезапустить',
      danger: true,
    }
  }
  if (kind === 'release') {
    return {
      title: 'Выпустить изменения',
      description: `git push/release flow для ${agentId}`,
      body: 'Действие выпускает накопленные изменения платформы. Запускать только когда текущая версия проверена.',
      confirm: 'Выпустить',
      danger: false,
    }
  }
  return {
    title: 'Подтянуть изменения',
    description: `git pull/restart flow для ${agentId}`,
    body: 'Агент подтянет доступные изменения из origin и перечитает runtime после перезапуска.',
    confirm: 'Подтянуть',
    danger: false,
  }
}

function AgentNameCell({ agent }: { agent: AgentHealth }) {
  return (
    <div style={tableAgentNameCellStyle}>
      <Link to={`/agents/${encodeURIComponent(agent.name)}`} style={tableAgentLinkStyle}>
        {agent.name}
        <FrmIcon name="chevron" size={12} style={{ opacity: 0.7, transform: 'rotate(-90deg)' }} />
      </Link>
      <TelegramLink agent={agent} />
    </div>
  )
}

function TelegramLink({ agent }: { agent: AgentHealth }) {
  if (!agent.telegramUrl) {
    return (
      <Tooltip content="Telegram username не задан">
        <span aria-label="Telegram недоступен" style={{ ...telegramLinkStyle, opacity: 0.42, cursor: 'not-allowed' }}>
          <TelegramIcon />
        </span>
      </Tooltip>
    )
  }
  return (
    <Tooltip content={agent.tgUsername ? `Открыть @${agent.tgUsername}` : 'Открыть Telegram'}>
      <a
        href={agent.telegramUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={`Открыть Telegram ${agent.name}`}
        style={telegramLinkStyle}
      >
        <TelegramIcon />
      </a>
    </Tooltip>
  )
}

function ColumnHeader({ label, hint }: { label: string; hint: string }) {
  return (
    <Tooltip content={hint}>
      <span style={columnHeaderStyle}>
        {label}
        <span aria-hidden style={columnHeaderHintStyle}>?</span>
      </span>
    </Tooltip>
  )
}

function ServicePill({ agent }: { agent: AgentHealth }) {
  return (
    <Tooltip content={`systemd: ${agent.raw.serviceStatus}`}>
      <span>
        <StatusPill status={agent.serviceUp ? 'ok' : 'danger'} label={agent.serviceUp ? 'active' : agent.raw.serviceStatus} />
      </span>
    </Tooltip>
  )
}

function StatusReasonLine({ agent }: { agent: AgentHealth }) {
  return (
    <span title={agent.statusReason} style={statusReasonLineStyle}>
      {statusReasonLabel(agent)}
    </span>
  )
}

function StatusReasonCell({ agent }: { agent: AgentHealth }) {
  return (
    <span title={agent.statusReason} style={statusReasonCellStyle}>
      {statusReasonLabel(agent)}
    </span>
  )
}

function SpecGrid({ agent }: { agent: AgentHealth }) {
  // В прототипе Агенты.dc.html:160-169 свёрнутая карточка показывает ровно
  // 2 поля: Модель (слева) + Активность (справа, выровнено по правому краю).
  // PID и Версия→прод видны только при раскрытии (см. ExpandedAgent).
  return (
    <div className="agent-card-spec-grid" style={agentCardSpecGridStyle}>
      <div style={specStyle}>
        <span style={microLabelStyle}>Модель</span>
        <Mono style={modelMonoStyle}>{agent.model}</Mono>
      </div>
      <div style={{ ...specStyle, alignItems: 'flex-end', justifyItems: 'end', textAlign: 'right' }}>
        <span style={microLabelStyle}>Последний ответ</span>
        <ActivityPill agent={agent} />
      </div>
    </div>
  )
}

function ExpandedAgent({ agent, onLogs }: { agent: AgentHealth; onLogs: (agentId: string) => void }) {
  // Mini-specs по прототипу Агенты.dc.html:426-433 (processCard.miniSpecs):
  // mode · PID · версия → прод · uptime · проект / спринт · cwd.
  return (
    <div style={expandedStyle}>
      <Surface variant="subtle" padding="sm" style={{ display: 'grid', gap: 8 }}>
        <span style={microLabelStyle}>mini-specs</span>
        <div style={miniRowStyle}>
          <span>mode</span>
          <Mono>{agent.mode}</Mono>
        </div>
        <div style={miniRowStyle}>
          <span>PID</span>
          <Mono>{String(agent.pid)}</Mono>
        </div>
        <div style={miniRowStyle}>
          <span>версия → прод</span>
          <VersionPill agent={agent} />
        </div>
        <div style={miniRowStyle}>
          <span>uptime</span>
          <Mono>{agent.uptime}</Mono>
        </div>
        <div style={miniRowStyle}>
          <span>проект / спринт</span>
          <Mono>{`${agent.project} · ${agent.sprint}`}</Mono>
        </div>
        <div style={miniRowStyle}>
          <span>cwd</span>
          <Mono>{agent.cwd}</Mono>
        </div>
      </Surface>
      <Surface variant="subtle" padding="sm" style={{ display: 'grid', gap: 8 }}>
        <span style={microLabelStyle}>mini-logs</span>
        <pre style={miniLogStyle}>
          {agent.error
            ? agent.error
            : `${agent.name}: service=${agent.raw.serviceStatus}; heartbeat=${heartbeatLabel(agent)}; activity=${activityLabel(agent)}; reason=${agent.statusReason}`}
        </pre>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button size="sm" variant="secondary" onClick={() => onLogs(agent.name)}>
            Открыть журнал
          </Button>
          {/* «Открыть полную карточку» — единственная точка перехода на /agents/:name из
              expanded-блока, как в прототипе Агенты.dc.html:191. В свёрнутой карточке имя
              само ведёт на роут — дубль в футере убран (см. T8 в audit-документе). */}
          <Link to={`/agents/${encodeURIComponent(agent.name)}`} style={smallLinkStyle}>
            Открыть полную карточку →
          </Link>
        </div>
      </Surface>
    </div>
  )
}

function VersionAction({
  agent,
  compact = false,
  releasing,
  pulling,
  onRelease,
  onPull,
}: {
  agent: AgentHealth
  compact?: boolean
  releasing: boolean
  pulling: boolean
  onRelease: (agentId: string) => void
  onPull: (agentId: string) => void
}) {
  // Используется только в таблице (колонка «Версия→прод», compact=true). В карточках
  // VersionPill уехал в expanded-блок, а action — в футер через VersionActionButton.
  if (agent.version.rel === 'ahead') {
    return (
      <div style={{ display: 'grid', gap: compact ? 4 : 8, marginTop: compact ? 0 : 12 }}>
        <VersionPill agent={agent} />
        <Button
          size="sm"
          variant="ghost"
          loading={releasing}
          leftIcon={<FrmIcon name="upload" size={15} />}
          style={tableVersionButtonStyle}
          onClick={() => onRelease(agent.name)}
        >
          Выпустить
        </Button>
      </div>
    )
  }
  if (agent.version.rel === 'behind') {
    return (
      <div style={{ display: 'grid', gap: compact ? 4 : 8, marginTop: compact ? 0 : 12 }}>
        <VersionPill agent={agent} />
        <Button
          size="sm"
          variant="ghost"
          loading={pulling}
          leftIcon={<DownloadIcon />}
          style={tableVersionButtonStyle}
          onClick={() => onPull(agent.name)}
        >
          Подтянуть
        </Button>
      </div>
    )
  }
  return <div style={{ marginTop: compact ? 0 : 12 }}><VersionPill agent={agent} /></div>
}

// Компактная action-кнопка для футера карточки (прототип Агенты.dc.html:196-205 +
// processCard.actFootStyle). Рендерится рядом с иконками Лог/Рестарт и появляется
// только при ahead/behind — для up/unknown не показывается.
function VersionActionButton({
  agent,
  releasing,
  pulling,
  onRelease,
  onPull,
}: {
  agent: AgentHealth
  releasing: boolean
  pulling: boolean
  onRelease: (agentId: string) => void
  onPull: (agentId: string) => void
}) {
  if (agent.version.rel === 'ahead') {
    return (
      <Button
        variant="ghost"
        size="sm"
        loading={releasing}
        leftIcon={<FrmIcon name="upload" size={15} />}
        onClick={() => onRelease(agent.name)}
        style={agentVersionButtonStyle}
      >
        Выпустить
      </Button>
    )
  }
  if (agent.version.rel === 'behind') {
    return (
      <Button
        variant="ghost"
        size="sm"
        loading={pulling}
        leftIcon={<DownloadIcon />}
        onClick={() => onPull(agent.name)}
        style={agentVersionButtonStyle}
      >
        Подтянуть
      </Button>
    )
  }
  return null
}

function VersionPill({ agent }: { agent: AgentHealth }) {
  const status = agent.version.rel === 'up' ? 'ok' : agent.version.rel === 'unknown' ? 'unknown' : 'changed'
  return <StatusPill status={status} label={agent.version.label} />
}

function AgentStatus({ agent }: { agent: AgentHealth }) {
  const status = agent.state === 'active' ? 'ok' : agent.state === 'stale' ? 'warning' : 'danger'
  return (
    <Tooltip content={agentStatusTooltip(agent)}>
      <span>
        <StatusPill status={status} label={agentStateLabel(agent)} />
      </span>
    </Tooltip>
  )
}

function ActivityPill({ agent }: { agent: AgentHealth }) {
  const status = agent.activityMin == null ? 'unknown' : agent.activityMin <= 15 ? 'ok' : agent.activityMin <= 60 ? 'warning' : 'danger'
  return (
    <Tooltip content={`последний успешный запрос: ${activityLabel(agent)} · heartbeat процесса: ${heartbeatLabel(agent)}`}>
      <span>
        <StatusPill status={status} label={activityLabel(agent)} style={activityPillStyle} />
      </span>
    </Tooltip>
  )
}

function ErrorLine({ error }: { error: string | null }) {
  // В прототипе Агенты.dc.html:171-173 строка ошибки в карточке не рендерится при
  // отсутствии error.
  if (!error) return null
  return (
    <span title={error} style={errorStyle}>
      {error}
    </span>
  )
}

function TableErrorCell({ error }: { error: string | null }) {
  if (!error) return <span style={tableNoErrorStyle}>нет</span>
  return <span title={error} style={tableErrorStyle}>{error}</span>
}

function AgentsSkeleton() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 330px), 1fr))', gap: 14 }}>
      {Array.from({ length: 6 }).map((_, idx) => (
        <WorkbenchPanel key={idx}>
          <Skeleton width="60%" height={18} />
          <Skeleton height={90} style={{ marginTop: 14 }} />
          <Skeleton width="45%" height={34} style={{ marginTop: 14 }} />
        </WorkbenchPanel>
      ))}
    </div>
  )
}

function summarizeAgents(agents: AgentHealth[]) {
  return {
    total: agents.length,
    active: agents.filter((agent) => agent.state === 'active' && agent.serviceUp).length,
    stale: agents.filter((agent) => agent.state === 'stale').length,
    error: agents.filter(hasCurrentAgentError).length,
    down: agents.filter((agent) => !agent.serviceUp).length,
    issues: agents.filter(isAgentIssue).length,
  }
}

function agentKey(agent: AgentHealth): string {
  return `${agent.name}-${agent.pid}`
}

function activityLabel(agent: AgentHealth): string {
  return ageLabelFromMinutes(agent.activityMin)
}

function heartbeatLabel(agent: AgentHealth): string {
  return ageLabelFromMinutes(agent.heartbeatMin)
}

function ageLabelFromMinutes(minutes: number | null): string {
  if (minutes == null) return 'unknown'
  if (minutes < 1) return '<1м'
  if (minutes < 60) return `${minutes}м`
  return `${Math.floor(minutes / 60)}ч ${minutes % 60}м`
}

function agentStateLabel(agent: AgentHealth): string {
  if (agent.state === 'active') return 'активен'
  if (agent.state === 'stale') return 'устарел'
  if (!agent.serviceUp) return 'down'
  return 'ошибка'
}

function statusReasonLabel(agent: AgentHealth): string {
  if (!agent.serviceUp) return `systemd: ${agent.raw.serviceStatus}`
  if (hasCurrentAgentError(agent)) return agent.error ? `текущая ошибка: ${agent.error}` : 'текущая ошибка'
  if (agent.state === 'stale') return `heartbeat устарел: ${heartbeatLabel(agent)}`
  if (agent.activityMin == null) return 'процесс жив; успешных запросов ещё не было'
  return `процесс жив; ответ ${activityLabel(agent)} назад`
}

function agentStatusTooltip(agent: AgentHealth): string {
  return `${statusReasonLabel(agent)} · raw: ${agent.statusReason}`
}

function hasCurrentAgentError(agent: AgentHealth): boolean {
  return agent.state === 'error' && agent.serviceUp
}

function isAgentIssue(agent: AgentHealth): boolean {
  return !agent.serviceUp || agent.state !== 'active'
}

function filterAgents(agents: AgentHealth[], filter: AgentFilter): AgentHealth[] {
  if (filter === 'all') return agents
  if (filter === 'issues') return agents.filter(isAgentIssue)
  if (filter === 'down') return agents.filter((agent) => !agent.serviceUp)
  if (filter === 'stale') return agents.filter((agent) => agent.state === 'stale')
  if (filter === 'error') return agents.filter(hasCurrentAgentError)
  return agents.filter((agent) => agent.state === 'active' && agent.serviceUp)
}

function formatDateTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function Mono({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <span style={{ ...monoStyle, ...style }}>{children}</span>
}

function LogIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 4h7l4 4v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
      <path d="M14 4v5h5" />
      <path d="M9 13h6M9 17h6" />
    </svg>
  )
}

function ChevronDownIcon({ open }: { open: boolean }) {
  return <FrmIcon name="chevron" size={13} style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 120ms' }} />
}

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  )
}

function TelegramIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.5 4.5 3.2 11.7c-.9.35-.86 1.6.06 1.88l4.7 1.43 1.8 5.4c.29.88 1.43 1.03 1.93.25l2.53-3.94 4.62 3.4c.78.57 1.9.14 2.08-.81l2.29-13.37c.17-.98-.78-1.76-1.71-1.44Z" />
      <path d="m8.1 14.9 6.18-5.1" />
    </svg>
  )
}

function CardsIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>
}

function TableIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
}

const agentsHeaderStyle: React.CSSProperties = {
  marginBottom: 18,
  flexWrap: 'wrap',
}

const agentsPageActionsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
}

const metricStripStyle: React.CSSProperties = {
  gap: 12,
}

const filterCountStyle: React.CSSProperties = {
  marginLeft: 'auto',
  color: 'var(--frm-text-3)',
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 12,
}

const agentCardsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 330px), 1fr))',
  gap: 14,
  alignItems: 'stretch',
  minWidth: 0,
  width: '100%',
}

const agentCardSpecGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: '13px 14px',
  alignItems: 'end',
  minWidth: 0,
}

const agentCardStyle: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 13,
  minWidth: 0,
  maxWidth: '100%',
  padding: 16,
  background: 'var(--frm-workbench-well, #0B1020)',
  border: '1px solid var(--frm-line-subtle, rgba(148,163,184,0.14))',
  borderRadius: 14,
  boxShadow: '0 8px 22px rgba(0,0,0,0.22)',
  color: 'var(--frm-text)',
}

const agentCardHeadStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 10,
  minWidth: 0,
}

const agentNameRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  minWidth: 0,
}

const agentCardDividerStyle: React.CSSProperties = {
  height: 1,
  background: 'var(--frm-line-subtle, rgba(148,163,184,0.14))',
}

const agentCardFootStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  flexWrap: 'wrap',
  minWidth: 0,
  marginTop: 'auto',
  padding: 9,
  background: 'var(--frm-workbench-well-2, #080C16)',
  border: '1px solid var(--frm-line-subtle, rgba(148,163,184,0.14))',
  borderRadius: 11,
}

const agentIconButtonStyle: React.CSSProperties = {
  border: '1px solid var(--frm-line-subtle, rgba(148,163,184,0.14))',
  background: 'transparent',
  color: 'var(--frm-text-3)',
}

const agentVersionButtonStyle: React.CSSProperties = {
  minHeight: 34,
  padding: '0 12px',
  border: '1px solid var(--frm-line-subtle, rgba(148,163,184,0.14))',
  borderRadius: 9,
  background: 'transparent',
  color: 'var(--frm-text-3)',
  fontSize: 12,
  fontWeight: 800,
}

const agentExpandButtonStyle: React.CSSProperties = {
  minHeight: 30,
  padding: '0 11px',
  border: '1px solid var(--frm-line-subtle, rgba(148,163,184,0.14))',
  borderRadius: 8,
  background: 'transparent',
  color: 'var(--frm-text-2)',
  fontSize: 12,
  fontWeight: 700,
}

const tableTextStyle: React.CSSProperties = {
  color: 'var(--frm-text-2)',
  fontSize: 13,
}

const tableAgentLinkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  color: 'var(--frm-accent)',
  textDecoration: 'none',
  fontWeight: 850,
  whiteSpace: 'nowrap',
}

const tableAgentNameCellStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  minWidth: 0,
}

const telegramLinkStyle: React.CSSProperties = {
  width: 26,
  height: 26,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  border: '1px solid var(--frm-line-subtle, rgba(148,163,184,0.14))',
  borderRadius: 8,
  color: 'var(--frm-accent-light, #7DDBFF)',
  background: 'rgba(56,189,248,0.08)',
  textDecoration: 'none',
}

const columnHeaderStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  minWidth: 0,
}

const columnHeaderHintStyle: React.CSSProperties = {
  width: 14,
  height: 14,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--frm-line-subtle, rgba(148,163,184,0.14))',
  borderRadius: '50%',
  color: 'var(--frm-text-3)',
  fontSize: 10,
  fontWeight: 850,
}

const tablePidStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflowWrap: 'normal',
  textOverflow: 'clip',
}

const tableIconButtonStyle: React.CSSProperties = {
  border: '1px solid var(--frm-line-subtle, rgba(148,163,184,0.14))',
  background: 'transparent',
  color: 'var(--frm-text-2)',
}

const tableVersionButtonStyle: React.CSSProperties = {
  minHeight: 24,
  padding: '0 9px',
  border: '1px solid currentColor',
  borderRadius: 7,
  background: 'transparent',
  fontSize: 11.5,
  fontWeight: 800,
}

const tableNoErrorStyle: React.CSSProperties = {
  color: 'var(--frm-text-3)',
  fontSize: 12,
}

const tableErrorStyle: React.CSSProperties = {
  display: 'block',
  maxWidth: 180,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--frm-danger)',
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 12,
}

const statusReasonLineStyle: React.CSSProperties = {
  display: 'block',
  marginTop: -2,
  color: 'var(--frm-text-3)',
  fontSize: 11.5,
  lineHeight: 1.35,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const statusReasonCellStyle: React.CSSProperties = {
  display: 'block',
  maxWidth: 166,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--frm-text-3)',
  fontSize: 12,
}

const agentNameLinkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  minWidth: 0,
  maxWidth: '100%',
  color: 'var(--frm-accent)',
  fontSize: 15,
  fontWeight: 800,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
}

const agentMetaStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--frm-text-3)',
  fontWeight: 600,
}

const smallLinkStyle: React.CSSProperties = {
  color: 'var(--frm-accent)',
  textDecoration: 'none',
  fontSize: 12.5,
  fontWeight: 800,
}

const specStyle: React.CSSProperties = {
  display: 'grid',
  gap: 5,
  minWidth: 0,
}

const monoStyle: React.CSSProperties = {
  display: 'inline-block',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  overflowWrap: 'anywhere',
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 12.5,
}

const microLabelStyle: React.CSSProperties = {
  color: 'var(--frm-text-3)',
  fontSize: 10.5,
  fontWeight: 850,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}

const footnoteStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 14,
  color: 'var(--frm-text-3)',
  fontSize: 12,
}

const footnoteDotStyle: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'var(--frm-accent)',
  animation: 'frm-pulse 2s infinite',
  flexShrink: 0,
}

const errorStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 12,
  color: 'var(--frm-danger)',
  fontSize: 12.5,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const expandedStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 12,
  marginTop: 14,
  paddingTop: 14,
  borderTop: '1px solid var(--frm-line-subtle)',
}

const miniRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '92px minmax(0, 1fr)',
  gap: 8,
  alignItems: 'baseline',
  color: 'var(--frm-text-3)',
  fontSize: 12,
}

const miniLogStyle: React.CSSProperties = {
  margin: 0,
  minHeight: 58,
  maxHeight: 96,
  overflow: 'auto',
  padding: 10,
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 8,
  background: 'var(--frm-workbench-well, #0B1020)',
  color: 'var(--frm-text-2)',
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 11.5,
  lineHeight: 1.45,
  whiteSpace: 'pre-wrap',
}

const modelMonoStyle: React.CSSProperties = {
  color: 'var(--frm-accent-light, #7DDBFF)',
  fontWeight: 600,
}

const activityPillStyle: React.CSSProperties = {
  justifySelf: 'end',
}

const logStyle: React.CSSProperties = {
  margin: 0,
  minHeight: 260,
  maxHeight: '60vh',
  overflow: 'auto',
  padding: 14,
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 10,
  background: 'var(--frm-workbench-well, #0B1020)',
  color: 'var(--frm-text-2)',
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 12,
  lineHeight: 1.55,
  whiteSpace: 'pre-wrap',
}
